package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ProfileUiState
import com.arena_web.data.dto.EventDto
import com.arena_web.data.dto.UserPostDto
import com.arena_web.data.dto.UserStoryDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.domain.repositories.CardRepository
import com.arena_web.domain.repositories.UserPostRepository
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.EMPTY_STATE_GLYPH_COLOR
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// Figma 9305:347696 — mobile Profile screen (Overview / Recent Events / Player Hub).
// Note: top bar + bottom nav are owned by MobilePageLayout. This composable only
// renders the scrollable column under the top bar.
@Composable
fun ProfileContentMobile(
    state: ProfileUiState,
    cardRepository: CardRepository? = null,
    userPostRepository: UserPostRepository? = null,
    onGoalsPeriodChanged: (String) -> Unit = {},
    onAssistsPeriodChanged: (String) -> Unit = {},
    onTeamChartPeriodChanged: (String) -> Unit = {},
    onMatchHistoryPageChange: (Int) -> Unit = {},
    onAvatarConfirmed: () -> Unit = {},
    onFollowingClick: () -> Unit = {},
    onFollowersClick: () -> Unit = {},
    onEditPersonalInfo: () -> Unit = {},
    onEditFocus: () -> Unit = {},
    onEditCard: () -> Unit = {},
    // ── Read-only / other-user mode. Every param below defaults to the
    // own-profile behaviour, so the existing Profile.kt caller is unchanged. ──
    /** false → visiting another user: the card edit pencil becomes a Follow /
     *  Chat button, Pricetags edit pencils hide, "Add post"/"Add story" hide,
     *  and the Cards tab drops the shop. */
    isOwnProfile: Boolean = true,
    isFollowing: Boolean = false,
    onFollow: () -> Unit = {},
    onChat: (() -> Unit)? = null,
    /** null → own posts (getMyPosts). Non-null → the visited user's posts. */
    loadPosts: (suspend () -> List<UserPostDto>)? = null,
    /** null → own stories (getMyStories). Non-null → the visited user's stories. */
    loadStories: (suspend () -> List<UserStoryDto>)? = null,
    canCreatePost: Boolean = isOwnProfile,
    canAddStory: Boolean = isOwnProfile,
    /** Non-null → Cards tab shows this user's owned cards read-only (getUserCards). */
    cardsUserId: String? = null,
    /** true → rendered inside BottomSheet: size to content + inner scroll
     *  instead of fillMaxSize + bottom-nav padding. */
    embeddedInSheet: Boolean = false,
) {
    val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "football"

    // Read-only interest-note dialog — handled here (not by callers) so both
    // own-profile and the other-user sheet surface "Preference details" the
    // same way when a noted interest chip is tapped.
    var viewNoteTarget by remember(state.user?.id) { mutableStateOf<String?>(null) }

    Column(
        modifier = Modifier
            .then(
                if (embeddedInSheet) Modifier
                    .fillMaxWidth()
                    // Fill the sheet panel up to its capped height (which leaves
                    // a top gap) and scroll inside; size to content when short.
                    // The bottom gap lives INSIDE the scroll (after the last
                    // block) so it only shows at the end and scrolls away.
                    .styleModifier {
                        // flex:1 so the scroll viewport fills the full-height
                        // sheet panel (the sheet opens at its capped height).
                        property("flex", "1 1 auto")
                        property("min-height", "0")
                    }
                else Modifier
                    .fillMaxSize()
            )
            .mGap(20)
            .styleModifier {
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
    ) {
        OverviewBlock(
            state = state,
            sportName = sportName,
            onAvatarConfirmed = onAvatarConfirmed,
            onFollowingClick = onFollowingClick,
            onFollowersClick = onFollowersClick,
            onEditPersonalInfo = onEditPersonalInfo,
            onEditFocus = onEditFocus,
            onEditCard = onEditCard,
            isOwnProfile = isOwnProfile,
            isFollowing = isFollowing,
            onFollow = onFollow,
            onChat = onChat,
            onInterestNoteClick = { viewNoteTarget = it },
        )

        RecentEventsBlock(
            matches = state.matchHistory,
            currentPage = state.matchHistoryPage,
            totalPages = com.arena_web.ui.sections.profile.matchHistoryTotalPages(
                state.matchHistoryTotal
            ),
            onPageChange = onMatchHistoryPageChange,
            isLoading = state.matchHistoryLoading,
        )

        PlayerHubBlock(
            state = state,
            sportName = sportName,
            userPostRepository = userPostRepository,
            cardRepository = cardRepository,
            onGoalsPeriodChanged = onGoalsPeriodChanged,
            onAssistsPeriodChanged = onAssistsPeriodChanged,
            loadPosts = loadPosts,
            loadStories = loadStories,
            canCreatePost = canCreatePost,
            canAddStory = canAddStory,
            cardsUserId = cardsUserId,
        )

        // Bottom spacer — emitted as a real sized Div at the END of the scroll
        // content. padding-bottom on a flex scroll container is dropped at the
        // scroll end on Chrome/Safari, so the last block would otherwise sit
        // flush against the fixed BottomNavBar (sheet edge when embedded). Same
        // trick as the mobile feed's trailing spacer.
        val bottomGap = if (embeddedInSheet) m(24) else "140px"
        Div(
            attrs = {
                style {
                    property("width", "100%")
                    property("flex", "0 0 $bottomGap")
                    property("height", bottomGap)
                }
            }
        )
    }

    // Read-only "Preference details" sheet — fixed overlay, so it can sit
    // outside the scroll column and still cover the viewport.
    val noteTarget = viewNoteTarget
    val targetNote = noteTarget?.let { state.interestNotes[it] }.orEmpty()
    if (noteTarget != null && targetNote.isNotBlank()) {
        MobileInterestNoteSheet(
            option = com.arena_web.ui.sections.profile.edit.InterestCatalog.lookup(noteTarget),
            note = targetNote,
            onDismiss = { viewNoteTarget = null }
        )
    }
}

// =============================================================================
// Overview block — Player card carousel + Following / Followers chips
// =============================================================================

private enum class OverviewTab { Profile, Pricetags, Awards }

@Composable
private fun OverviewBlock(
    state: ProfileUiState,
    sportName: String,
    onAvatarConfirmed: () -> Unit,
    onFollowingClick: () -> Unit = {},
    onFollowersClick: () -> Unit = {},
    onEditPersonalInfo: () -> Unit = {},
    onEditFocus: () -> Unit = {},
    onEditCard: () -> Unit = {},
    isOwnProfile: Boolean = true,
    isFollowing: Boolean = false,
    onFollow: () -> Unit = {},
    onChat: (() -> Unit)? = null,
    onInterestNoteClick: (String) -> Unit = {},
) {
    val t = LocalAppStrings.current
    val user = state.user
    val mainCard = user?.mainProfileCard

    var activeTab by remember { mutableStateOf(OverviewTab.Profile) }
    // Cards carousel index — main card is always at 0; the rest of the
    // gallery (if any) advances on tap of the right-edge pagination dots.
    // Mobile-app ProfileContent parity: the carousel shows only USABLE cards —
    // frozen (subscription-lapsed) ones are excluded entirely (they stay,
    // PRO+-gated, in the card gallery below). Without a subscription that
    // collapses the deck to the single default card with the avatar.
    val cards = state.profileCards.filter { !it.frozen }
        .ifEmpty { listOfNotNull(state.profileCards.firstOrNull()) }
    var carouselIndex by remember { mutableStateOf(0) }
    // Reset to first card whenever the underlying gallery changes (avoid
    // pointing past the end after a card delete).
    LaunchedEffect(cards.size) {
        if (carouselIndex >= cards.size) carouselIndex = 0
    }
    // Card flip — front of card shows the player art, back shows the radar
    // and stats. Reset to front whenever the active card changes so the
    // previous card's flipped state doesn't carry over to the new one.
    var isFlipped by remember(carouselIndex) { mutableStateOf(false) }


    // Avatar visibility — small staggered delays around the flip so the
    // avatar doesn't linger past the 90° crossover (mobile WebKit issue
    // where the avatar's `will-change: transform` puts it on its own
    // compositing layer that ignores the parent face's
    // backface-visibility). 100 ms after click-to-flip we drop the
    // avatar; 300 ms after the flip-back click (~enough for the rotation
    // to land back at the front) we restore it.
    var showAvatar by remember(carouselIndex) { mutableStateOf(true) }
    LaunchedEffect(isFlipped) {
        if (isFlipped) {
            kotlinx.coroutines.delay(100)
            showAvatar = false
        } else {
            kotlinx.coroutines.delay(300)
            showAvatar = true
        }
    }

    // Figma 9305:347718 — px-14 pt-12 pb-12. We use gap-14 (tighter than
    // Figma's 22) so the whole Overview block fits above the bottom nav
    // on standard phone viewports without scroll.
    ContainerCard(
        paddingTop = 12,
        paddingBottom = 12,
        paddingHorizontal = 14,
        gap = 14,
    ) {
        // Header: "Overview" + segmented control
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SectionHeader(t.profileContentMobileOverview, Modifier.flexGrow(1))
            OverviewSegmentedControl(
                active = activeTab,
                onSelect = { activeTab = it }
            )
        }

        // Player card gallery — bespoke mobile carousel built around a
        // single keyed loop so each card keeps its DOM node + Composable
        // identity for the whole deck cycle. Cards never migrate between
        // separate slots; only their CSS transform/opacity changes as
        // they rotate through Front → PeekClose → PeekFar → Hidden roles.
        // That removes the image re-mount and "showOverlays" flicker the
        // desktop's split FlippableCard + peek-loop design caused on
        // mobile, and the CSS transitions handle the snap-back/promote
        // animation in one place.
        val frontProfile = cards.getOrNull(carouselIndex)
        // Never surface a frozen card as the resolved main card. When the
        // gallery's main entry (index 0) is frozen — owner's subscription
        // lapsed — fall back to the first non-frozen (default) card so the
        // profile still shows an unlocked card up top. Default cards are
        // never frozen, so a non-frozen entry is always a safe fallback.
        val resolvedMain = if (mainCard?.frozen == true) {
            cards.firstOrNull { !it.frozen } ?: mainCard
        } else mainCard
        val frontCard = frontProfile ?: resolvedMain
        val hasSportProfile = state.sportProfile != null || state.aggregateRating != null

        val displayName = (user?.fullName?.split(" ")?.lastOrNull()
            ?: user?.username ?: "").uppercase()
        val ovr = (state.sportProfile?.overallRating
            ?: state.aggregateRating?.overallRating ?: 0.0).toInt()
        val cardPosition = state.sportProfile?.position?.name ?: ""
        // Per-sport stats for the back of the card — sort by attribute
        // display order,
        // map to 3-letter uppercase labels (e.g. "PAS", "DRI", "SHO").
        // Recomputes whenever the user switches sports → sportProfile
        // changes → these PlayerStat entries update. "All" falls back to
        // the cross-sport aggregate's per-attribute means.
        val cardStats = state.sportProfile?.ratings
            ?.sortedBy { it.attribute.displayOrder }
            ?.map {
                com.arena_web.ui.components.cards.PlayerStat(
                    it.value.toInt(),
                    it.attribute.name.take(3).uppercase()
                )
            }
            ?: state.aggregateRating?.attributes
                ?.sortedBy { it.displayOrder }
                ?.map {
                    com.arena_web.ui.components.cards.PlayerStat(
                        it.value.toInt(),
                        it.name.take(3).uppercase()
                    )
                }
            ?: emptyList()

        // Tab content area — grows to fit its content. Page-level scrolling
        // in `ProfileContentMobile` handles overflow.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                }
        ) {
            when (activeTab) {
                OverviewTab.Profile -> ProfileTabContent(
                    cards = cards,
                    carouselIndex = carouselIndex,
                    onCarouselIndexChange = { carouselIndex = it },
                    frontCard = frontCard,
                    playerName = displayName,
                    rating = ovr,
                    position = cardPosition,
                    sportName = sportName,
                    hasSportProfile = hasSportProfile,
                    isFlipped = isFlipped,
                    onFlipToggle = { isFlipped = !isFlipped },
                    showAvatar = showAvatar,
                    followingCount = state.followingCount?.toInt() ?: 0,
                    followersCount = state.followersCount?.toInt() ?: 0,
                    cardStats = cardStats,
                    onFollowingClick = onFollowingClick,
                    onFollowersClick = onFollowersClick,
                    onEditCard = onEditCard,
                    isOwnProfile = isOwnProfile,
                    isFollowing = isFollowing,
                    onFollow = onFollow,
                    onChat = onChat,
                )
                OverviewTab.Pricetags -> PricetagsTabContent(
                    state = state,
                    onEditPersonalInfo = onEditPersonalInfo,
                    onEditFocus = onEditFocus,
                    isOwnProfile = isOwnProfile,
                    isFollowing = isFollowing,
                    onFollow = onFollow,
                    onChat = onChat,
                    onInterestNoteClick = onInterestNoteClick,
                )
                OverviewTab.Awards -> AwardsTabContent(awards = state.awards)
            }
        }
    }
}

// =============================================================================
// Profile tab — card gallery (carousel + dots) + Following / Followers chips.
// Lives inside the fixed-height Overview tab slot.
// =============================================================================
@Composable
private fun ProfileTabContent(
    cards: List<com.arena_web.data.dto.ProfileCardDto>,
    carouselIndex: Int,
    onCarouselIndexChange: (Int) -> Unit,
    frontCard: com.arena_web.data.dto.ProfileCardDto?,
    playerName: String,
    rating: Int,
    position: String,
    sportName: String,
    hasSportProfile: Boolean,
    isFlipped: Boolean,
    onFlipToggle: () -> Unit,
    showAvatar: Boolean,
    followingCount: Int,
    followersCount: Int,
    cardStats: List<com.arena_web.ui.components.cards.PlayerStat>,
    onFollowingClick: () -> Unit = {},
    onFollowersClick: () -> Unit = {},
    onEditCard: () -> Unit = {},
    isOwnProfile: Boolean = true,
    isFollowing: Boolean = false,
    onFollow: () -> Unit = {},
    onChat: (() -> Unit)? = null,
) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            property("width", "100%")
            property("position", "relative")
            property("--scale", "calc(var(--mobile-scale) * 0.85)")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", m(8))
            // Reserve room for the flip/edit overlay buttons that sit 8 px
            // ABOVE the card (top: m(-8)). Without this padding the parent
            // tab Box (overflow-y: auto, mHeight 420) clips the top of the
            // buttons because the card otherwise hugs y=0.
            property("padding-top", m(12))
        }
    }) {
        Box(
            modifier = Modifier.styleModifier { property("position", "relative") },
            contentAlignment = Alignment.Center
        ) {
            MobileCardDeck(
                cards = cards,
                currentIndex = carouselIndex.coerceIn(
                    0,
                    (cards.size - 1).coerceAtLeast(0)
                ),
                fallbackCard = frontCard,
                playerName = playerName,
                rating = rating,
                position = position,
                sportName = sportName,
                hasSportProfile = hasSportProfile,
                isFlipped = isFlipped,
                showFrontAvatar = showAvatar,
                cardStats = cardStats,
                onNext = {
                    if (cards.size > 1) {
                        onCarouselIndexChange((carouselIndex + 1) % cards.size)
                    }
                },
                onPrev = {
                    if (cards.size > 1) {
                        onCarouselIndexChange((carouselIndex - 1 + cards.size) % cards.size)
                    }
                },
            )

            // Overlay buttons on the front card corners.
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", m(-8))
                        property("left", "0")
                        property("z-index", "10")
                    }
                    .onClick { onFlipToggle() }
            ) {
                OverlayCircleButton(icon = AppIcons.CardFlip)
            }
            // Right-corner overlay: own profile → edit pencil; another user →
            // Follow (add-friend) button, swapped for a Chat button once the
            // viewer follows them. Figma 8636:332131 (Button_Add_Friend).
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", m(-8))
                        property("right", "0")
                        property("z-index", "10")
                    }
            ) {
                when {
                    isOwnProfile -> Box(modifier = Modifier.onClick { onEditCard() }) {
                        OverlayCircleButton(icon = AppIcons.Pencil, iconSize = 12)
                    }
                    isFollowing && onChat != null -> Box(modifier = Modifier.onClick { onChat() }) {
                        OverlayCircleButton(icon = AppIcons.Chat, iconSize = 16)
                    }
                    else -> Box(modifier = Modifier.onClick { onFollow() }) {
                        FollowAddButton()
                    }
                }
            }
        }

        if (cards.size > 1) {
            Row(
                modifier = Modifier.mGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                for (i in 0 until cards.size) {
                    val active = i == carouselIndex
                    Box(
                        modifier = Modifier
                            .then(if (active) Modifier.mSize(6) else Modifier.mSize(4))
                            .mBorderRadius(100)
                            .styleModifier {
                                property(
                                    "background",
                                    if (active) "#0061FF" else "WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40)"
                                )
                            }
                            .cursor(Cursor.Pointer)
                            .onClick { onCarouselIndexChange(i) }
                    )
                }
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().mGap(8).mPaddingTop(8),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            FollowChip(label = t.profileContentMobileFollowing, count = followingCount, onClick = onFollowingClick)
            FollowChip(label = t.profileContentMobileFollowers, count = followersCount, onClick = onFollowersClick)
        }
    }
}

// =============================================================================
// Pricetags tab — "About" basic info (name/location/languages/bio) + Focus &
// Activities (sport / activity / lifestyle interest chips). Reuses the desktop
// `BasicInfoPanel` and `InterestsPanel`, which already render the empty state
// when the user hasn't filled in any of these fields.
// =============================================================================
@Composable
private fun PricetagsTabContent(
    state: ProfileUiState,
    onEditPersonalInfo: () -> Unit = {},
    onEditFocus: () -> Unit = {},
    isOwnProfile: Boolean = true,
    isFollowing: Boolean = false,
    onFollow: () -> Unit = {},
    onChat: (() -> Unit)? = null,
    onInterestNoteClick: (String) -> Unit = {},
) {
    // `--scale: var(--mobile-scale)` makes the desktop `s()` helpers render
    // at 1:1 with the Figma design (which is drawn at the 390-px mobile
    // baseline), instead of the global ~0.4 px clamp.
    Div(attrs = {
        style {
            property("width", "100%")
            property("--scale", "var(--mobile-scale)")
        }
    }) {
        // Mobile-specific edit button — Figma 8428:86808: 36 px circle
        // with `Button/border/gradient/primary` 0.3 px stroke and
        // `Interactive/Secondary` fill, holding a 12 px pencil glyph.
        // Reuses the existing `OverlayCircleButton` helper (same shape we
        // use over the player card).
        val editButton: @Composable (() -> Unit) -> Unit = { onClick ->
            Box(modifier = Modifier.onClick { onClick() }) {
                OverlayCircleButton(icon = AppIcons.Pencil, iconSize = 12)
            }
        }

        // Figma 8327:3070 — Basic Info content + a single gradient divider,
        // followed by the Focus & Activities content. The two sections are
        // visually flat (no inner panel cards); the only background chrome
        // is the outer ContainerCard around the whole Overview block. The
        // divider re-uses the same `linear-gradient(90deg, transparent →
        // 30% grey → transparent)` style that the desktop BasicInfoPanel
        // used internally between languages and the bio (now suppressed
        // here via `flat = true` so we don't paint it twice).
        Column(
            modifier = Modifier.fillMaxWidth().mGap(12)
        ) {
            com.arena_web.ui.sections.profile.BasicInfoPanel(
                user = state.user,
                isOwnProfile = isOwnProfile,
                languages = state.languages,
                onEditClick = if (isOwnProfile) onEditPersonalInfo else null,
                renderEditButton = editButton,
                flat = true,
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .mHeight(1)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, " +
                                "rgba(148, 149, 152, 0.3) 50%, " +
                                "rgba(148, 149, 152, 0) 100%)"
                        )
                    }
            )
            com.arena_web.ui.sections.profile.InterestsPanel(
                isOwnProfile = isOwnProfile,
                isFollowing = isFollowing,
                onFollowClick = if (isOwnProfile) null else onFollow,
                onChatClick = if (isOwnProfile) null else onChat,
                onEditClick = if (isOwnProfile) onEditFocus else null,
                interestSports = state.interestSports,
                interestActivities = state.interestActivities,
                interestLifestyle = state.interestLifestyle,
                interestNotes = state.interestNotes,
                onInterestNoteClick = onInterestNoteClick,
                renderEditButton = editButton,
                flat = true,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

// =============================================================================
// Awards tab — earned-award grid; tapping an award flips it in place to show
// the back face (mirrors the mobile app's Awards tab).
// =============================================================================
@Composable
private fun AwardsTabContent(awards: List<com.arena_web.data.dto.UserAwardDto>) {
    Column(modifier = Modifier.fillMaxWidth()) {
        com.arena_web.ui.sections.profile.AwardsGridContent(awards = awards)
    }
}

// =============================================================================
// Mobile-specific card deck — unified keyed loop replacement for the desktop
// CardCarousel. Every gallery card has a stable DOM node + Composable identity
// for its entire lifetime; only the CSS transform/opacity changes as it cycles
// through Front → PeekClose → PeekFar → Hidden roles.
//
// This avoids the desktop pattern where the front card lives in FlippableCard
// and peeks live in a separate forEach loop — every swap migrated cards
// between distinct DOM parents, which caused image re-mounts, `LaunchedEffect`
// restarts, and the visible "reload flash" the user reported on phones.
// =============================================================================

private enum class DeckRole { Front, PeekClose, PeekFar, Hidden }

@Composable
private fun MobileCardDeck(
    cards: List<com.arena_web.data.dto.ProfileCardDto>,
    currentIndex: Int,
    fallbackCard: com.arena_web.data.dto.ProfileCardDto?,
    playerName: String,
    rating: Int,
    position: String,
    sportName: String,
    hasSportProfile: Boolean,
    isFlipped: Boolean,
    /** When false, the FRONT card's avatar is suppressed (rendered with
     *  `avatarUrl = null`). Used by OverviewBlock to hide the avatar during
     *  the 3D flip animation so mobile WebKit's compositor doesn't show it
     *  lingering past the 90° crossover. Peeks ignore this flag. */
    showFrontAvatar: Boolean,
    /** Per-sport stats already mapped to the card's 3-letter labels. Forwarded
     *  to PlayerCardBack so the radar/stats face reflects the user's current
     *  sport selection — empty list when the user has no sport profile yet. */
    cardStats: List<com.arena_web.ui.components.cards.PlayerStat>,
    onNext: () -> Unit,
    onPrev: () -> Unit,
) {
    // No deck yet — render the fallback (main / sport-empty card) statically.
    if (cards.isEmpty()) {
        Box(
            modifier = Modifier
                .sWidth(PlayerCardSize.Large.w)
                .sHeight(PlayerCardSize.Large.h),
            contentAlignment = Alignment.Center
        ) {
            PlayerCard(
                playerName = playerName,
                rating = rating,
                position = position,
                sport = sportName,
                size = PlayerCardSize.Large,
                avatarUrl = if (hasSportProfile) fallbackCard?.avatarUrl ?: AppIcons.EmptyAvatar else null,
                hideRating = true,
                stats = emptyList(),
                cardAssetsBasePath = fallbackCard?.assetsBasePath,
                cardTextColor = fallbackCard?.textColor,
            )
        }
        return
    }

    val swipeThreshold = 50.0
    var dragX by remember { mutableStateOf(0.0) }
    var dragging by remember { mutableStateOf(false) }
    var skipTransition by remember { mutableStateOf(false) }
    val startX = remember { mutableStateOf(0.0) }
    val activePointer = remember { mutableStateOf<Int?>(null) }
    val scope = rememberCoroutineScope()

    // Cubic ease drives the peek promotion as the user drags.
    val peekProgress = (kotlin.math.abs(dragX) / swipeThreshold).coerceIn(0.0, 1.0)
    val peekEase = peekProgress * peekProgress * peekProgress

    Box(
        modifier = Modifier
            .sWidth(PlayerCardSize.Large.w)
            .sHeight(PlayerCardSize.Large.h)
            .styleModifier { property("position", "relative") }
    ) {
        cards.forEachIndexed { idx, card ->
            key(card.id) {
                val distance = (idx - currentIndex + cards.size) % cards.size
                val role: DeckRole = when {
                    distance == 0 -> DeckRole.Front
                    distance == 1 -> DeckRole.PeekClose
                    cards.size > 2 && distance == cards.size - 1 -> DeckRole.PeekFar
                    else -> DeckRole.Hidden
                }

                // Hidden cards are still mounted (keeps their <img> cache
                // warm) but visually clipped — that way when they cycle
                // into a visible role there is no remount delay.
                val baseTx: Double
                val baseScale: Double
                val baseOpacity: Double
                val baseZ: Int
                when (role) {
                    DeckRole.Front -> {
                        baseTx = 0.0; baseScale = 1.0; baseOpacity = 1.0; baseZ = 3
                    }
                    DeckRole.PeekClose -> {
                        baseTx = 14.0; baseScale = 0.92; baseOpacity = 0.55; baseZ = 2
                    }
                    DeckRole.PeekFar -> {
                        baseTx = 28.0; baseScale = 0.84; baseOpacity = 0.35; baseZ = 1
                    }
                    DeckRole.Hidden -> {
                        baseTx = 28.0; baseScale = 0.80; baseOpacity = 0.0; baseZ = 0
                    }
                }

                val tx: Double
                val scale: Double
                val opacity: Double
                val rotate: Double
                when (role) {
                    DeckRole.Front -> {
                        // Front card follows the drag with a small rotation
                        // and micro-shrink for tactile feedback.
                        tx = baseTx + dragX
                        scale = baseScale * (1.0 -
                            (kotlin.math.abs(dragX) / 2400.0).coerceAtMost(0.04))
                        opacity = baseOpacity
                        rotate = (dragX * 0.05).coerceIn(-9.0, 9.0)
                    }
                    DeckRole.PeekClose -> {
                        // Promotes toward the Front slot.
                        tx = baseTx + (0.0 - baseTx) * peekEase
                        scale = baseScale + (1.0 - baseScale) * peekEase
                        opacity = baseOpacity + (1.0 - baseOpacity) * peekEase
                        rotate = 0.0
                    }
                    DeckRole.PeekFar -> {
                        // Promotes toward the PeekClose slot.
                        tx = baseTx + (14.0 - baseTx) * peekEase
                        scale = baseScale + (0.92 - baseScale) * peekEase
                        opacity = baseOpacity + (0.55 - baseOpacity) * peekEase
                        rotate = 0.0
                    }
                    DeckRole.Hidden -> {
                        tx = baseTx; scale = baseScale; opacity = baseOpacity; rotate = 0.0
                    }
                }

                val transition = when {
                    dragging || skipTransition -> "none"
                    else -> "transform 0.36s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease"
                }

                Div(attrs = {
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property(
                            "transform",
                            "translate(${tx}px, 0) rotate(${rotate}deg) scale($scale)"
                        )
                        property("transform-origin", "center 80%")
                        property("opacity", "$opacity")
                        property("z-index", "$baseZ")
                        property("transition", transition)
                        property("will-change", "transform, opacity")
                        property(
                            "pointer-events",
                            if (role == DeckRole.Front && cards.size > 1) "auto" else "none"
                        )
                        property("touch-action", "pan-y")
                        property("user-select", "none")
                        property("-webkit-user-select", "none")
                        property("visibility", if (role == DeckRole.Hidden) "hidden" else "visible")
                    }

                    if (role == DeckRole.Front && cards.size > 1) {
                        ref { el ->
                            val onDown: (org.w3c.dom.events.Event) -> Unit = { ev ->
                                val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                                dragging = true
                                startX.value = pe.clientX.toDouble()
                                dragX = 0.0
                                activePointer.value = pe.pointerId
                                runCatching { el.setPointerCapture(pe.pointerId) }
                            }
                            val onMove: (org.w3c.dom.events.Event) -> Unit = { ev ->
                                if (dragging) {
                                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                                    if (activePointer.value == null
                                        || activePointer.value == pe.pointerId
                                    ) {
                                        dragX = pe.clientX.toDouble() - startX.value
                                    }
                                }
                            }
                            val onUp: (org.w3c.dom.events.Event) -> Unit = { ev ->
                                if (dragging) {
                                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                                    dragging = false
                                    activePointer.value = null
                                    runCatching { el.releasePointerCapture(pe.pointerId) }
                                    val dx = dragX

                                    when {
                                        dx <= -swipeThreshold -> {
                                            scope.launch {
                                                // Slide the front card off-screen
                                                // in the swipe direction with the
                                                // normal CSS transition.
                                                dragX = -460.0
                                                kotlinx.coroutines.delay(220)
                                                // Disable transitions and swap
                                                // the index. The OLD front (now
                                                // PeekFar role) snaps from off-
                                                // screen to its new resting slot
                                                // instantly — invisible because
                                                // it was already off-screen.
                                                skipTransition = true
                                                onNext()
                                                dragX = 0.0
                                                kotlinx.coroutines.delay(20)
                                                skipTransition = false
                                            }
                                        }
                                        dx >= swipeThreshold -> {
                                            scope.launch {
                                                dragX = 460.0
                                                kotlinx.coroutines.delay(220)
                                                skipTransition = true
                                                onPrev()
                                                dragX = 0.0
                                                kotlinx.coroutines.delay(20)
                                                skipTransition = false
                                            }
                                        }
                                        else -> {
                                            // Below threshold — snap back.
                                            dragX = 0.0
                                        }
                                    }
                                }
                            }
                            el.addEventListener("pointerdown", onDown)
                            el.addEventListener("pointermove", onMove)
                            el.addEventListener("pointerup", onUp)
                            el.addEventListener("pointercancel", onUp)
                            el.addEventListener("pointerleave", onUp)
                            onDispose {
                                el.removeEventListener("pointerdown", onDown)
                                el.removeEventListener("pointermove", onMove)
                                el.removeEventListener("pointerup", onUp)
                                el.removeEventListener("pointercancel", onUp)
                                el.removeEventListener("pointerleave", onUp)
                            }
                        }
                    }
                }) {
                    Box(
                        modifier = Modifier
                            .sWidth(PlayerCardSize.Large.w)
                            .sHeight(PlayerCardSize.Large.h)
                            .styleModifier { property("position", "relative") },
                        contentAlignment = Alignment.Center
                    ) {
                        com.arena_web.ui.sections.profile.FlippableCard(
                            isFlipped = role == DeckRole.Front && isFlipped,
                            front = {
                                // For the front card, suppress the avatar
                                // when showFrontAvatar=false (during the
                                // flip animation). Peeks always show their
                                // avatar normally. Frozen cards never show an
                                // avatar — the frost overlay takes over.
                                // Frozen cards keep their REAL generated avatar;
                                // the PRO+ chip overlay gates them.
                                val frontAvatarUrl = when {
                                    !hasSportProfile -> null
                                    role == DeckRole.Front && !showFrontAvatar -> null
                                    else -> card.avatarUrl ?: AppIcons.EmptyAvatar
                                }
                                PlayerCard(
                                    playerName = playerName,
                                    rating = rating,
                                    position = position,
                                    sport = sportName,
                                    size = PlayerCardSize.Large,
                                    avatarUrl = frontAvatarUrl,
                                    hideRating = true,
                                    stats = emptyList(),
                                    cardAssetsBasePath = card.assetsBasePath,
                                    cardTextColor = card.textColor,
                                )
                            },
                            back = {
                                com.arena_web.ui.sections.profile.PlayerCardBack(
                                    rating = rating,
                                    position = position,
                                    sportName = sportName,
                                    stats = cardStats,
                                    cardAssetsBasePath = card.assetsBasePath,
                                    cardTextColor = card.textColor,
                                )
                            },
                        )
                        // Frost/lock overlay for a subscription-locked card —
                        // shown over the front face (hidden while flipped so the
                        // stats face stays legible).
                        val showFrostFront = !(role == DeckRole.Front && isFlipped)
                        if (card.frozen && showFrostFront) {
                            com.arena_web.ui.components.cards.FrozenCardOverlay()
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun OverviewSegmentedControl(
    active: OverviewTab,
    onSelect: (OverviewTab) -> Unit,
) {
    SegmentedControlShell {
        SegmentedIcon(AppIcons.Profile, active = active == OverviewTab.Profile) { onSelect(OverviewTab.Profile) }
        SegmentedIcon(AppIcons.Pricetags, active = active == OverviewTab.Pricetags) { onSelect(OverviewTab.Pricetags) }
        SegmentedIcon(AppIcons.Ribbon, active = active == OverviewTab.Awards) { onSelect(OverviewTab.Awards) }
    }
}

@Composable
private fun FollowChip(label: String, count: Int, onClick: () -> Unit = {}) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .mWidth(126).mHeight(36)
            .mBorderRadius(22)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier.mGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("white-space", "nowrap") }
            )
            Box(
                modifier = Modifier
                    .mSize(20)
                    .mBorderRadius(100)
                    .styleModifier { property("background-color", sportColors.primary.toString()) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    count.toString(),
                    modifier = Modifier
                        .mFontSize(14)
                        .color(sportColors.onPrimary)
                        .styleModifier { property("line-height", "1") }
                )
            }
        }
    }
}

@Composable
private fun OverlayCircleButton(icon: String, iconSize: Int = 16) {
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = icon, modifier = Modifier.mSize(iconSize).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
    }
}

// Follow / "add friend" button shown over another user's card — Figma
// 8636:332131 (Button_Add_Friend): the same 36-px gradient circle as
// OverlayCircleButton holding a 16-px person glyph, with a small blue "+"
// badge pinned to the top-right corner.
@Composable
private fun FollowAddButton() {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("position", "relative")
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = AppIcons.Profile, modifier = Modifier.mSize(16).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
        Box(
            modifier = Modifier
                .mSize(13)
                .mBorderRadius(100)
                .styleModifier {
                    property("position", "absolute")
                    property("top", m(1))
                    property("right", m(1))
                    property("background-color", sportColors.primary.toString())
                    property("border", "1px solid var(--l-s3, #02040B)")
                },
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.mSize(7).styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
    }
}

// =============================================================================
// Recent Events block
// =============================================================================

@Composable
private fun RecentEventsBlock(
    matches: List<EventDto>,
    currentPage: Int,
    totalPages: Int,
    onPageChange: (Int) -> Unit,
    isLoading: Boolean,
) {
    val t = LocalAppStrings.current
    // Figma 9412:161417 — px-12 pt-16 pb-12, gap-18 between header and list,
    // plus inner 12-gap between cards.
    ContainerCard(
        paddingTop = 16,
        paddingBottom = 12,
        paddingHorizontal = 12,
        gap = 18,
        blur = true,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().mPaddingLeft(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SectionHeader(t.profileContentMobileRecentEvents, Modifier.flexGrow(1))
            // Pagination control — matches the desktop RecentMatchesSection:
            // chevron-left, current/total label, chevron-right. Only shown
            // when there's at least one page worth of matches to navigate.
            if (totalPages >= 1) {
                Row(
                    modifier = Modifier.mGap(6),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    MobileNavArrow(
                        enabled = currentPage > 0,
                        direction = "left",
                        onClick = { if (currentPage > 0) onPageChange(currentPage - 1) },
                    )
                    SpanText(
                        "${currentPage + 1}/$totalPages",
                        modifier = Modifier
                            .mFontSize(13)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                    MobileNavArrow(
                        enabled = currentPage < totalPages - 1,
                        direction = "right",
                        onClick = {
                            if (currentPage < totalPages - 1) onPageChange(currentPage + 1)
                        },
                    )
                }
            }
        }

        // Match list. Same precedence as the desktop RecentMatchesSection:
        // loader wins over both the old matches and the empty state — so
        // the user gets a clear "loading" signal the moment they tap an
        // arrow, instead of seeing the previous page's cards lingering
        // until the network response arrives.
        Column(
            modifier = Modifier.fillMaxWidth().mGap(12)
        ) {
            when {
                isLoading -> {
                    Box(
                        modifier = Modifier.fillMaxWidth().mHeight(150),
                        contentAlignment = Alignment.Center
                    ) {
                        // Intentionally empty — no loading indicator (user pref).
                    }
                }
                matches.isEmpty() -> {
                    Column(
                        modifier = Modifier.fillMaxWidth().mPadding(v = 16, h = 0).mGap(12),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        // Calendar glyph in the canonical empty-state colour
                        // (mask — this is an m()-scaled page, not a TintedGlyph
                        // which sizes via s()).
                        Box(
                            modifier = Modifier.mSize(56).styleModifier {
                                property("background-color", EMPTY_STATE_GLYPH_COLOR)
                                property("-webkit-mask", "url('${AppIcons.EmptyCalendarLarge}') no-repeat center / contain")
                                property("mask", "url('${AppIcons.EmptyCalendarLarge}') no-repeat center / contain")
                            }
                        )
                        Column(
                            modifier = Modifier.fillMaxWidth().mGap(8),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            SpanText(
                                t.profileContentMobileNoEvents,
                                modifier = Modifier
                                    .mFontSize(18)
                                    .fontWeight(AppTypography.Medium)
                                    .mLineHeight(24)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                    .textAlign(TextAlign.Center),
                            )
                            SpanText(
                                t.profileContentMobileNoEventsBody,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .mFontSize(14)
                                    .fontWeight(AppTypography.Regular)
                                    .mLineHeight(22)
                                    .color(Color("var(--arena-text-muted)"))
                                    .textAlign(TextAlign.Center),
                            )
                        }
                    }
                }
                else -> {
                    matches.take(2).forEach { match ->
                        MobileMatchCard(match)
                    }
                }
            }
        }
    }
}

@Composable
private fun MobileNavArrow(
    enabled: Boolean,
    direction: String,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .mSize(28)
            .mBorderRadius(100)
            .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
            .styleModifier {
                property(
                    "border",
                    "0.3px solid rgba(255, 255, 255, ${if (enabled) "0.4" else "0.15"})"
                )
                property(
                    "background",
                    AppGradients.InteractiveSecondary
                )
                property("opacity", if (enabled) "1" else "0.4")
                property("transition", "opacity 0.2s")
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        Icon(
            src = AppIcons.ChevronForward,
            modifier = Modifier
                .mSize(12)
                .styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) }
                .then(
                    if (direction == "left") Modifier.styleModifier {
                        property("transform", "rotate(180deg)")
                    } else Modifier
                )
        )
    }
}

@Composable
private fun MobileMatchCard(match: EventDto) {
    val t = LocalAppStrings.current
    // Navigate via window.location (not rememberPageContext): this card also
    // renders inside the profile BottomSheet, which is mounted outside a @Page
    // — rememberPageContext() throws there. The event detail is a full route,
    // so a hard navigation is fine in both contexts.
    val resultText = when (match.result?.uppercase()) {
        "WIN" -> t.profileContentMobileResultWin
        "DRAW" -> t.profileContentMobileResultDraw
        "LOSS" -> t.profileContentMobileResultLoss
        else -> null
    }
    val date = formatShortDate(match.dateTime)
    val location = match.city?.takeIf { it.isNotBlank() } ?: match.address
    val sportLabel = (match.customSportName ?: match.sportName).ifBlank { t.profileContentMobileSportFallback }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .mHeight(150)
            .mBorderRadius(18)
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .onClick { kotlinx.browser.window.location.assign("${Routes.EVENTS}/${match.id}") }
            .styleModifier { property("position", "relative") }
    ) {
        // Stadium background image
        Box(
            modifier = Modifier.fillMaxSize().styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("background-image", "url('${match.imageUrl ?: AppIcons.EventFieldBackground}')")
                property("background-size", "cover")
                property("background-position", "center")
            }
        )
        // Gradient border ring (mask-composite — survives any backdrop-filter).
        Box(
            modifier = Modifier.fillMaxSize().styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", m(18))
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
                property("z-index", "2")
            }
        )
        // Dark left-fade overlay
        Box(
            modifier = Modifier.fillMaxSize().styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property(
                    "background",
                    "linear-gradient(90deg, rgba(0,0,0,0.9) 10%, rgba(0,0,0,0.7) 38%, " +
                        "rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.3) 76%, rgba(0,0,0,0.2) 91%)"
                )
            }
        )

        // Content
        Column(
            modifier = Modifier
                .fillMaxSize()
                .mPadding(12)
                .mGap(12)
                .styleModifier {
                    property("position", "relative")
                    property("justify-content", "space-between")
                    property("z-index", "1")
                }
        ) {
            Column(modifier = Modifier.fillMaxWidth().mGap(6)) {
                Row(
                    modifier = Modifier.fillMaxWidth().mGap(8),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        modifier = Modifier.flexGrow(1).mGap(8).styleModifier { property("min-width", "0") },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            match.title,
                            modifier = Modifier
                                .mFontSize(18)
                                .fontWeight(AppTypography.Medium)
                                .color(Color.white)
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                        if (resultText != null) {
                            Box(
                                modifier = Modifier.mWidth(1).mHeight(20).styleModifier {
                                    property("background", "rgba(255,255,255,0.3)")
                                }
                            )
                            ResultPill(resultText)
                        }
                    }
                    SportTagPill(sport = sportLabel, tier = if (match.isPro) "Pro+" else null)
                }
                Row(
                    modifier = Modifier.fillMaxWidth().mGap(4),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // theme-invariant: address row sits on the photo card
                    Icon(src = AppIcons.Location, modifier = Modifier.mSize(14))
                    SpanText(
                        location.ifBlank { "—" },
                        modifier = Modifier
                            .mFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .color(org.jetbrains.compose.web.css.Color("#BED6F5"))
                            .styleModifier {
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                    )
                }
            }

            Row(
                modifier = Modifier.mGap(18),
                verticalAlignment = Alignment.CenterVertically
            ) {
                match.format?.let { f ->
                    MetaItem(
                        icon = AppIcons.FootballField,
                        text = t.profileContentMobileFormat(f),
                        iconWidth = 27,
                        iconHeight = 14
                    )
                }
                MetaItem(icon = AppIcons.Calendar, text = date, iconSize = 14)
                MetaItem(
                    icon = AppIcons.People,
                    text = t.profileContentMobileMembers(match.participantsCount),
                    iconSize = 14
                )
            }
        }
    }
}

@Composable
private fun ResultPill(text: String) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .mHeight(32)
            .mBorderRadius(28)
            .styleModifier {
                property("padding", "${m(6)} ${m(10)}")
                property(
                    "background",
                    "linear-gradient(rgba(11,22,46,0.8), rgba(11,22,46,0.8)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("border", "0.3px solid transparent")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(14)
                .fontWeight(AppTypography.SemiBold)
                .color(sportColors.metricsPrimary)
        )
    }
}

@Composable
private fun SportTagPill(sport: String, tier: String?) {
    Row(
        modifier = Modifier
            .mHeight(32)
            .mBorderRadius(100)
            .mGap(8)
            .styleModifier {
                property("padding", "${m(10)} ${m(14)}")
                property(
                    "background",
                    "linear-gradient(rgba(11,22,46,0.8), rgba(11,22,46,0.8)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("border", "0.3px solid transparent")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            sport,
            modifier = Modifier
                .mFontSize(14)
                .fontWeight(AppTypography.Medium)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5")) // theme-invariant: dark pill over photo
                .styleModifier { property("white-space", "nowrap") }
        )
        if (tier != null) {
            Box(
                modifier = Modifier.mWidth(1).mHeight(14).styleModifier {
                    property("background", "rgba(255,255,255,0.3)")
                }
            )
            SpanText(
                tier,
                modifier = Modifier
                    .mFontSize(14)
                    .fontWeight(AppTypography.SemiBold)
                    .color(Color.white)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun MetaItem(
    icon: String,
    text: String,
    iconSize: Number? = null,
    iconWidth: Number? = null,
    iconHeight: Number? = null,
) {
    Row(
        modifier = Modifier.mGap(6),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val iconMod = when {
            iconWidth != null && iconHeight != null ->
                Modifier.mWidth(iconWidth).mHeight(iconHeight)
            iconSize != null -> Modifier.mSize(iconSize)
            else -> Modifier.mSize(14)
        }
        Icon(src = icon, modifier = iconMod) // theme-invariant: over photo row
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(12)
                .fontWeight(AppTypography.Regular)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5")) // theme-invariant: over photo row
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// =============================================================================
// Player Hub block — stories + grid/list toolbar + photo grid
// =============================================================================

private enum class HubTab { Content, Stats, Cards }

// Mirrors the desktop ContentViewMode (PlayerHubSection.kt:1242). Selected by
// the Apps / Menu toggle in the Player Hub toolbar.
private enum class ContentViewMode { Grid, List }

@Composable
private fun PlayerHubBlock(
    state: ProfileUiState,
    sportName: String,
    userPostRepository: UserPostRepository?,
    cardRepository: CardRepository?,
    onGoalsPeriodChanged: (String) -> Unit,
    onAssistsPeriodChanged: (String) -> Unit,
    loadPosts: (suspend () -> List<UserPostDto>)? = null,
    loadStories: (suspend () -> List<UserStoryDto>)? = null,
    canCreatePost: Boolean = true,
    canAddStory: Boolean = true,
    cardsUserId: String? = null,
) {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()
    var activeTab by remember { mutableStateOf(HubTab.Content) }
    var viewMode by remember { mutableStateOf(ContentViewMode.Grid) }

    var posts by remember { mutableStateOf<List<UserPostDto>>(emptyList()) }
    var stories by remember { mutableStateOf<List<UserStoryDto>>(emptyList()) }
    // Bumped after a story is created so the row refetches the fresh list.
    var storiesReload by remember { mutableStateOf(0) }
    // Bumped after a post is created so the grid/list refetches the fresh list.
    var postsReload by remember { mutableStateOf(0) }

    // null loaders → own profile (getMyPosts / getMyStories). Non-null →
    // the visited user's public posts / stories.
    LaunchedEffect(loadPosts, postsReload) {
        posts = loadPosts?.invoke()
            ?: userPostRepository?.getMyPosts(mapOf("page" to "1", "size" to "20"))
                ?.getOrNull()?.items.orEmpty()
    }
    LaunchedEffect(storiesReload, loadStories) {
        stories = loadStories?.invoke()
            ?: container.userStoryRepository?.getMyStories()?.getOrNull().orEmpty()
    }

    val userName = (state.user?.fullName?.takeIf { it.isNotBlank() }
        ?: state.user?.username ?: "")
    val userAvatarUrl = state.user?.avatarUrl

    // Story-viewer bottom sheet. The tapped circle's index (within the
    // visible take(4) window) doubles as the "open" flag: non-null = visible.
    var storyViewerIndex by remember { mutableStateOf<Int?>(null) }
    // "Who saw this story?" sheet (Figma 8682:456190) — the story id whose
    // viewers are shown; non-null = the second sheet is open over the viewer.
    var viewersStoryId by remember { mutableStateOf<String?>(null) }
    // Create-story bottom sheet (Figma 8218:322321), opened from the "+" circle.
    var showCreateStory by remember { mutableStateOf(false) }
    // Create-post bottom sheet (Figma 8664:434161), opened from "Add post".
    var showCreatePost by remember { mutableStateOf(false) }
    // Mini player card shown in the sheet's identity row — the user's main
    // profile card, mapped onto the UserCardDto shape StoryViewerContent reads.
    val storyCard = state.user?.mainProfileCard?.let { c ->
        com.arena_web.data.dto.UserCardDto(
            name = c.templateName,
            cardImageUrl = c.cardImageUrl,
            assetsBasePath = c.assetsBasePath,
            textColor = c.textColor,
            avatarUrl = c.avatarUrl,
        )
    }

    ContainerCard(blur = true) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SectionHeader(t.profileContentMobilePlayerHub, Modifier.flexGrow(1))
            HubSegmentedControl(active = activeTab, onSelect = { activeTab = it })
        }

        when (activeTab) {
            HubTab.Content -> ContentTabBody(
                stories = stories,
                posts = posts,
                viewMode = viewMode,
                onViewModeChange = { viewMode = it },
                userName = userName,
                userAvatarUrl = userAvatarUrl,
                onStoryClick = { idx -> storyViewerIndex = idx },
                onAddStoryClick = { showCreateStory = true },
                onAddPostClick = { showCreatePost = true },
                canCreatePost = canCreatePost,
                canAddStory = canAddStory,
                // Own profile only: "⋮" in the card header → Delete.
                // Optimistic removal; reload restores on failure.
                onDeletePost = userPostRepository?.takeIf { canCreatePost }?.let { repo ->
                    { post ->
                        posts = posts.filter { it.id != post.id }
                        scope.launch {
                            repo.deletePost(post.id).onFailure { postsReload++ }
                        }
                    }
                },
            )
            HubTab.Stats -> StatsTabContent(
                state = state,
                sportName = sportName,
                onGoalsPeriodChanged = onGoalsPeriodChanged,
                onAssistsPeriodChanged = onAssistsPeriodChanged,
            )
            HubTab.Cards -> CardsTabContent(
                sportName = sportName,
                cardRepository = cardRepository,
                cardsUserId = cardsUserId,
            )
        }
    }

    // Story viewer — rendered as a sibling of the (backdrop-blurred)
    // ContainerCard so its position:fixed overlay anchors to the viewport
    // rather than being clipped by the card's backdrop-filter. The visible
    // window mirrors StoriesRow's take(4) so the tapped index lines up.
    val openIndex = storyViewerIndex
    if (openIndex != null) {
        val visibleStories = stories.take(4)
        if (visibleStories.isNotEmpty()) {
            MobileStoryViewerSheet(
                stories = visibleStories,
                initialIndex = openIndex,
                title = userName,
                sportName = sportName,
                activeCard = storyCard,
                avatarUrl = userAvatarUrl,
                onDismiss = { storyViewerIndex = null },
                onLikeClick = container.userStoryRepository?.let { repo ->
                    { storyId ->
                        // Optimistic toggle: flip local state immediately, then
                        // reconcile with the server; revert on failure.
                        val before = stories
                        stories = stories.map { st ->
                            if (st.id != storyId) st
                            else st.copy(
                                isLikedByMe = !st.isLikedByMe,
                                likesCount = (st.likesCount + if (st.isLikedByMe) -1 else 1)
                                    .coerceAtLeast(0),
                            )
                        }
                        scope.launch {
                            repo.likeStory(storyId).onFailure { stories = before }
                        }
                    }
                },
                onSeenByClick = { id -> viewersStoryId = id },
                externallyPaused = viewersStoryId != null,
                // Own profile only: "⋮" in the identity row → Delete.
                onDeleteClick = container.userStoryRepository?.takeIf { canAddStory }?.let { repo ->
                    { storyId ->
                        storyViewerIndex = null
                        stories = stories.filter { it.id != storyId }
                        scope.launch {
                            repo.deleteStory(storyId).onFailure { storiesReload++ }
                        }
                    }
                },
            )
        }
    }

    // "Who saw this story?" — a SECOND sheet popped over the viewer on a
    // Seen-by tap. Sibling of the viewer sheet so its fixed scrim/panel aren't
    // trapped by the viewer sheet's transformed panel.
    viewersStoryId?.let { vsId ->
        MobileStoryViewersSheet(
            storyId = vsId,
            storyRepository = container.userStoryRepository,
            sportName = sportName,
            onDismiss = { viewersStoryId = null },
        )
    }

    // Create-story sheet — sibling of the blurred ContainerCard for the same
    // fixed-positioning reason. Refetches the row on success.
    val storyRepo = container.userStoryRepository
    if (showCreateStory && storyRepo != null) {
        MobileCreateStorySheet(
            storyRepository = storyRepo,
            onDismiss = { showCreateStory = false },
            onCreated = {
                showCreateStory = false
                storiesReload++
            },
        )
    }

    // Create-post sheet — sibling of the blurred ContainerCard for the same
    // fixed-positioning reason. Refetches the grid on success.
    if (showCreatePost && userPostRepository != null) {
        MobileCreatePostSheet(
            postRepository = userPostRepository,
            onDismiss = { showCreatePost = false },
            onCreated = {
                showCreatePost = false
                postsReload++
            },
        )
    }
}

@Composable
private fun ContentTabBody(
    stories: List<UserStoryDto>,
    posts: List<UserPostDto>,
    viewMode: ContentViewMode,
    onViewModeChange: (ContentViewMode) -> Unit,
    userName: String,
    userAvatarUrl: String?,
    onStoryClick: (Int) -> Unit = {},
    onAddStoryClick: () -> Unit = {},
    onAddPostClick: () -> Unit = {},
    canCreatePost: Boolean = true,
    canAddStory: Boolean = true,
    onDeletePost: ((UserPostDto) -> Unit)? = null,
) {
    // Stories row
    StoriesRow(
        stories = stories,
        onStoryClick = onStoryClick,
        onAddStoryClick = onAddStoryClick,
        canAddStory = canAddStory,
    )

    // Toolbar: grid/list toggle + Add post
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            modifier = Modifier.mGap(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Figma 9305:347921 — Apps container 18, inset 9.38% → visible 14.6.
            // ic_apps.svg viewBox is tight 16.25×16.25 (no internal padding),
            // so we render at the visible-glyph size, not the container.
            ViewModeButton(
                icon = AppIcons.Apps,
                active = viewMode == ContentViewMode.Grid,
                iconSize = 14.6,
                onClick = { onViewModeChange(ContentViewMode.Grid) },
            )
            // Figma 7917:401386 — Menu container 20, inset 25%/12.5% → visible 15×10.
            // ic_menu.svg viewBox is tight 18×12 (3:2); mSize(15) fills width 15,
            // preserveAspectRatio renders height at 10 automatically.
            ViewModeButton(
                icon = AppIcons.Menu,
                active = viewMode == ContentViewMode.List,
                iconSize = 15,
                onClick = { onViewModeChange(ContentViewMode.List) },
            )
        }
        if (canCreatePost) AddPostButton(onClick = onAddPostClick)
    }

    // Content area — switches between grid and vertical list. Same data
    // source; List adds the post chrome (header, body, collage, reactions).
    // Each mode filters the posts differently, so the empty check matches.
    when (viewMode) {
        ContentViewMode.Grid ->
            if (posts.none { it.images.isNotEmpty() }) PostsEmptyState(canCreatePost)
            else PostsGrid(posts = posts)
        ContentViewMode.List ->
            if (posts.none { it.images.isNotEmpty() || it.content.isNotBlank() }) PostsEmptyState(canCreatePost)
            else PostsList(
                posts = posts,
                userName = userName,
                userAvatarUrl = userAvatarUrl,
                onDeleteClick = onDeletePost,
            )
    }
}

// Empty posts placeholder — image-stack icon + prompt. Copy depends on whether
// it's the viewer's own profile. Uses m() helpers (mobile-scale page).
@Composable
private fun PostsEmptyState(canCreatePost: Boolean) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().mPadding(v = 32, h = 0).mGap(12),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Canonical muted-blue empty-state tint (matches desktop TintedGlyph).
        Box(
            modifier = Modifier.mWidth(72).mHeight(63).styleModifier {
                property("background-color", EMPTY_STATE_GLYPH_COLOR)
                property("-webkit-mask", "url('${AppIcons.EmptyImagesStack}') no-repeat center / contain")
                property("mask", "url('${AppIcons.EmptyImagesStack}') no-repeat center / contain")
            }
        )
        Column(
            modifier = Modifier.fillMaxWidth().mGap(8),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.profileContentMobileNoPostsTitle,
                modifier = Modifier
                    .mFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .mLineHeight(26)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .textAlign(TextAlign.Center),
            )
            SpanText(
                if (canCreatePost) t.profileContentMobileNoPostsOwnBody
                else t.profileContentMobileNoPostsOtherBody,
                modifier = Modifier
                    .fillMaxWidth()
                    .mFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .mLineHeight(22)
                    .color(Color("var(--arena-text-muted)"))
                    .textAlign(TextAlign.Center),
            )
        }
    }
}

// =============================================================================
// Stats tab — Figma 8376:98785 (line/area) + 8381:186653 (bars). One time
// switcher per chart, ball-tooltip on hover/tap, sub-label identifying the
// stat. The existing desktop LineChart / BarChart already draw the body
// + tooltip; we just wrap them at mobile scale (`--scale: var(--mobile-scale)`)
// so the internal `s()` helpers resolve at the 390-px mobile baseline.
// =============================================================================
@Composable
private fun StatsTabContent(
    state: ProfileUiState,
    sportName: String,
    onGoalsPeriodChanged: (String) -> Unit,
    onAssistsPeriodChanged: (String) -> Unit,
) {
    val ballIcon = AppIcons.ballSmall(sportName)
    val targetIcon = AppIcons.StatTarget

    // Vertical stack — bar chart first, line chart below. Stays inside
    // the parent ContainerCard's horizontal padding so each chart card
    // gets a comfortable margin against the outer block edge.
    Div(attrs = {
        style {
            property("--scale", "var(--mobile-scale)")
            property("width", "100%")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", m(20))
            property("padding-top", m(4))
        }
    }) {
        // Figma 8381:186653 — bar chart (Assists) shown FIRST.
        // barWidthMultiplier=0.7 narrows the bars so a year-view (12 bars)
        // gets visible gaps; the desktop default (28 px) is too wide once
        // we sit inside a phone-width panel.
        StatsChartPanel(
            period = state.assistsChartPeriod,
            onPeriodChanged = onAssistsPeriodChanged,
        ) {
            val data = com.arena_web.ui.sections.profile.resolveDataPoints(
                state.statsCharts[state.assistsChartPeriod]?.dataPoints,
                state.assistsChartPeriod,
            )
            com.arena_web.ui.sections.profile.BarChart(
                dataPoints = data,
                statKey = "assists",
                period = state.assistsChartPeriod,
                chartHeight = 240,
                barWidthMultiplier = 0.7,
                tooltipIcon = ballIcon,
                tooltipIconSize = 13,
            )
        }

        // Figma 8376:98785 — line/area chart (Goals) shown to the right of
        // the bar chart, revealed on horizontal swipe. chartHeight=210
        // accounts for the LineChart's separate axis-dots row (~10) and
        // label row (~20), so the panel's TOTAL height matches the bar
        // chart's 240 and the two charts visually share the same baseline.
        // Tooltip uses the crosshair/target icon at 13 px (vs the default
        // 17) so it reads as a precision marker on the line peak rather
        // than the ball used in the bar chart.
        StatsChartPanel(
            period = state.goalsChartPeriod,
            onPeriodChanged = onGoalsPeriodChanged,
        ) {
            val data = com.arena_web.ui.sections.profile.resolveDataPoints(
                state.statsCharts[state.goalsChartPeriod]?.dataPoints,
                state.goalsChartPeriod,
            )
            com.arena_web.ui.sections.profile.LineChart(
                dataPoints = data,
                statKey = "goals",
                period = state.goalsChartPeriod,
                chartHeight = 210,
                tooltipIcon = targetIcon,
                tooltipIconSize = 13,
                clickOnly = true,
            )
        }
    }
}

@Composable
private fun StatsChartPanel(
    period: String,
    onPeriodChanged: (String) -> Unit,
    chart: @Composable () -> Unit,
) {
    // Each panel is its own bordered card so the time switcher + chart
    // read as one grouped block, separated visually from the sibling
    // chart. Same border + gradient palette as the outer ContainerCard
    // (rgba(41, 52, 138, 0.2) hairline + soft blue radial background).
    Div(attrs = {
        style {
            property("width", "100%")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", m(16))
            property("padding", "${m(14)} ${m(4)} ${m(16)}")
            property("border", "1px solid rgba(41, 52, 138, 0.2)")
            property("border-radius", m(22))
            property(
                "background",
                "linear-gradient(-20deg, rgba(52, 119, 250, 0.018) 27%, rgba(2, 4, 11, 0.3) 140%)"
            )
            property("box-sizing", "border-box")
        }
    }) {
        // Time switcher (Week / Month / Year). The desktop TimeSwitcher
        // already uses the AppGradients.ButtonBorderGradientPrimary mask
        // and InteractiveSecondary fill — at mobile scale it lands ~36 px
        // tall, matching Figma 2644:3332.
        com.arena_web.ui.components.navigation.TimeSwitcher(period, onPeriodChanged)

        Box(modifier = Modifier.fillMaxWidth()) {
            chart()
        }
    }
}

// =============================================================================
// Cards tab — Figma 8383:187091. Active & Bought row + Available for purchase
// row + "Select a Card" CTA. Reuses the desktop `CardShopViewModel` so cards
// load over the same endpoint and purchases land in the same backend state.
// =============================================================================
@Composable
private fun CardsTabContent(
    sportName: String,
    cardRepository: CardRepository?,
    cardsUserId: String? = null,
) {
    val t = LocalAppStrings.current
    // No repository wired in this caller — render an empty state instead of
    // crashing on the rememberViewModel call below.
    if (cardRepository == null) {
        Box(
            modifier = Modifier.fillMaxWidth().mHeight(220),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileContentMobileCardsUnavailable,
                modifier = Modifier
                    .mFontSize(14)
                    .color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }

    // Another user's profile — show their owned cards read-only, no shop.
    if (cardsUserId != null) {
        OtherUserCardsTab(
            cardRepository = cardRepository,
            userId = cardsUserId,
            sportName = sportName,
        )
        return
    }

    val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID) ?: ""
    val viewModel = com.arena_web.core.viewmodel.rememberViewModel(key = sportId) {
        com.arena_web.core.viewmodel.CardShopViewModel(cardRepository)
    }
    val state by viewModel.uiState.collectAsState()

    // Captured for the purchase portal (a separate composition with no providers).
    val appContainer = LocalAppContainer.current
    val toastState = com.arena_web.ui.components.notifications.LocalToastState.current
    val cardSportColors = LocalSportColors.current

    var confirmCard by remember { mutableStateOf<com.arena_web.data.dto.UserCardDto?>(null) }

    // Subscribed → the shop is included in the plan: claim every template
    // silently so it lands in Active & Bought, and drop the Available-for-
    // purchase section (with its divider). It only re-appears as a manual
    // fallback when a claim request failed.
    val isSubscribed by appContainer.isSubscribed.collectAsState()
    LaunchedEffect(Unit) { appContainer.ensureSubscriptionLoaded() }
    LaunchedEffect(isSubscribed, state.shopCards, state.autoClaimFailed) {
        if (isSubscribed && state.shopCards.isNotEmpty() && !state.autoClaimFailed) {
            viewModel.autoClaimShop()
        }
    }
    val showShopSection = state.shopCards.isNotEmpty() && (!isSubscribed || state.autoClaimFailed)

    // Tapping a frozen (subscription-lapsed) owned card → mono paywall.
    var frozenPaywallOpen by remember { mutableStateOf(false) }
    com.arena_web.ui.components.overlay.SubscribeModal(
        isOpen = frozenPaywallOpen,
        onClose = { frozenPaywallOpen = false },
        source = "card_frozen",
    )

    Div(attrs = {
        style {
            property("width", "100%")
            property("--scale", "var(--mobile-scale)")
        }
    }) {
        Column(
            modifier = Modifier.fillMaxWidth().mGap(28).mPaddingTop(4)
        ) {
            when {
                state.isLoading -> {
                    Box(
                        modifier = Modifier.fillMaxWidth().mHeight(220),
                        contentAlignment = Alignment.Center
                    ) {
                        // Intentionally empty — no loading indicator (user pref).
                    }
                }

                state.loadError != null -> {
                    Box(
                        modifier = Modifier.fillMaxWidth().mHeight(220),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            state.loadError ?: t.profileContentMobileCardsLoadFailed,
                            modifier = Modifier
                                .mFontSize(14)
                                .color(com.arena_web.ui.theme.ThemeVars.Text3)
                        )
                    }
                }

                state.myCards.isEmpty() && state.shopCards.isEmpty() -> {
                    Box(
                        modifier = Modifier.fillMaxWidth().mHeight(220),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            t.profileContentMobileNoCardsAvailable,
                            modifier = Modifier
                                .mFontSize(14)
                                .color(com.arena_web.ui.theme.ThemeVars.Text3)
                        )
                    }
                }

                else -> {
                    // Owned cards as a vertical wrap grid (mobile-app parity:
                    // the "Active & Bought / N owned" header is gone and the
                    // row no longer scrolls horizontally).
                    CardsGrid {
                        state.myCards.forEach { card ->
                            OwnedMobileCard(
                                card = card,
                                sportName = sportName,
                                onFrozenClick = { frozenPaywallOpen = true },
                            )
                        }
                    }

                    // Figma 8383:187113 — divider + Available for purchase
                    // section, followed by the Select a Card CTA. Hidden for
                    // subscribed viewers: the shop is auto-claimed into
                    // Active & Bought (unless a claim failed — manual fallback).
                    if (showShopSection) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .mHeight(1)
                                .styleModifier {
                                    property(
                                        "background",
                                        "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, " +
                                            "rgba(148, 149, 152, 0.3) 50%, " +
                                            "rgba(148, 149, 152, 0) 100%)"
                                    )
                                }
                        )

                        CardsSection(
                            title = t.profileContentMobileAvailableForPurchase,
                            meta = t.profileContentMobileNewDesigns,
                        ) {
                            CardsGrid {
                                state.shopCards.forEach { card ->
                                    ShopMobileCard(
                                        card = card,
                                        sportName = sportName,
                                        onClick = { confirmCard = card },
                                    )
                                }
                            }
                        }
                    }

                }
            }
        }
    }

    // Purchase confirmation — rendered into document.body as a fullscreen
    // overlay, mirrors the desktop flow (CardShopSection.kt:56).
    if (confirmCard != null) {
        val card = confirmCard!!
        DisposableEffect(card.id) {
            val portalRoot = kotlinx.browser.document.createElement("div")
            kotlinx.browser.document.body?.appendChild(portalRoot)
            val composition = org.jetbrains.compose.web.renderComposable(portalRoot) {
                // Re-provide the locals (the portal is its own composition) and
                // collect the shop state HERE so the dialog stays reactive — the
                // responsive PurchaseCardModal then handles the mobile sheet, the
                // subscription check and opening the paywall on a 402.
                CompositionLocalProvider(
                    LocalAppContainer provides appContainer,
                    com.arena_web.ui.components.notifications.LocalToastState provides toastState,
                    LocalSportColors provides cardSportColors,
                ) {
                    val st by viewModel.uiState.collectAsState()
                    LaunchedEffect(st.myCards) {
                        if (st.myCards.any { it.templateId == card.templateId }) confirmCard = null
                    }
                    com.arena_web.ui.components.overlay.PurchaseCardModal(
                        card = card,
                        sportName = sportName,
                        purchasing = st.purchasing,
                        purchaseError = st.purchaseError,
                        onConfirm = { c -> viewModel.purchaseCard(c.templateId) },
                        onDismiss = {
                            if (!st.purchasing) {
                                viewModel.clearPurchaseError()
                                confirmCard = null
                            }
                        },
                    )
                }
            }
            onDispose {
                composition.dispose()
                portalRoot.remove()
            }
        }
    }
}

// Read-only Cards tab for another user's profile — their owned cards via the
// public `getUserCards(userId)` endpoint. No shop row, no purchase flow.
@Composable
private fun OtherUserCardsTab(
    cardRepository: CardRepository,
    userId: String,
    sportName: String,
) {
    val t = LocalAppStrings.current
    var cards by remember(userId) { mutableStateOf<List<com.arena_web.data.dto.UserCardDto>?>(null) }
    LaunchedEffect(userId) {
        cards = cardRepository.getUserCards(userId).getOrNull().orEmpty()
    }

    val loaded = cards
    Div(attrs = {
        style {
            property("width", "100%")
            property("--scale", "var(--mobile-scale)")
        }
    }) {
        Column(modifier = Modifier.fillMaxWidth().mGap(28).mPaddingTop(4)) {
            when {
                loaded == null -> Box(
                    modifier = Modifier.fillMaxWidth().mHeight(220),
                    contentAlignment = Alignment.Center
                ) {
                    // Intentionally empty — no loading indicator (user pref).
                }

                loaded.isEmpty() -> Box(
                    modifier = Modifier.fillMaxWidth().mHeight(220),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.profileContentMobileNoCardsYet,
                        modifier = Modifier.mFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }

                else -> {
                    // Vertical wrap grid, no "Cards / N owned" header (parity
                    // with the own-profile branch and the mobile app).
                    var frozenPaywallOpen by remember { mutableStateOf(false) }
                    com.arena_web.ui.components.overlay.SubscribeModal(
                        isOpen = frozenPaywallOpen,
                        onClose = { frozenPaywallOpen = false },
                        source = "card_frozen",
                    )
                    CardsGrid {
                        loaded.forEach { card ->
                            OwnedMobileCard(
                                card = card,
                                sportName = sportName,
                                onFrozenClick = { frozenPaywallOpen = true },
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CardsSection(
    title: String,
    meta: String,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().mGap(24)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().mPaddingLeft(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .flexGrow(1)
                    .mFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .mLineHeight(18)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            SpanText(
                meta,
                modifier = Modifier
                    .mFontSize(12)
                    .fontWeight(AppTypography.Regular)
                    .mLineHeight(12)
                    .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
        content()
    }
}

// Vertical wrap grid — cards flow into rows and wrap down with the page
// scroll (was a horizontal scroll row; mobile-app parity). Cards keep their
// natural size via `flex: 0 0 auto`.
@Composable
private fun CardsGrid(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("box-sizing", "border-box")
            property("width", "100%")
            property("display", "flex")
            property("flex-direction", "row")
            property("flex-wrap", "wrap")
            property("gap", m(12))
            property("row-gap", m(16))
            property("justify-content", "center")
            property("padding-top", m(4))
            property("padding-bottom", m(4))
        }
    }) {
        content()
    }
}

@Composable
private fun OwnedMobileCard(
    card: com.arena_web.data.dto.UserCardDto,
    sportName: String,
    /** Frozen (subscription-lapsed) card tapped → open the paywall. */
    onFrozenClick: (() -> Unit)? = null,
) {
    // Trust the backend `frozen` flag — a generated avatar on the default
    // card is frozen too. Frozen cards keep their real frame at full opacity,
    // gated with a PRO+ pill + centered lock (mobile-app parity).
    val frozen = card.frozen
    Div(attrs = {
        style {
            property("flex", "0 0 auto")
            if (frozen && onFrozenClick != null) property("cursor", "pointer")
        }
        if (frozen && onFrozenClick != null) onClick { onFrozenClick() }
    }) {
        Box(
            modifier = Modifier.styleModifier { property("position", "relative") },
            contentAlignment = Alignment.Center
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = PlayerCardSize.Arena,
                cardFrameUrl = card.cardImageUrl,
                cardAssetsBasePath = card.assetsBasePath,
                cardTextColor = card.textColor,
            )
            if (frozen) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("top", m(6))
                        property("right", m(6))
                    },
                ) {
                    com.arena_web.ui.components.overlay.ProPlusPill(onPrimary = false)
                }
                Box(
                    modifier = Modifier
                        .mSize(40)
                        .mBorderRadius(100)
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "50%")
                            property("left", "50%")
                            property("transform", "translate(-50%, -50%)")
                            property("background", "rgba(11, 22, 46, 0.8)")
                            property("backdrop-filter", "blur(4px)")
                            property("pointer-events", "none")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    com.arena_web.ui.sections.profile.edit.GradientBorderRing(radiusPx = 100)
                    SpanText("🔒", modifier = Modifier.mFontSize(18))
                }
            }
        }
    }
}

@Composable
private fun ShopMobileCard(
    card: com.arena_web.data.dto.UserCardDto,
    sportName: String,
    onClick: () -> Unit,
) {
    Div(attrs = {
        style {
            property("flex", "0 0 auto")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", m(12))
            property("cursor", "pointer")
        }
        onClick { onClick() }
    }) {
        Box(
            modifier = Modifier.styleModifier { property("position", "relative") },
            contentAlignment = Alignment.Center
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = PlayerCardSize.Arena,
                cardFrameUrl = card.cardImageUrl,
                cardAssetsBasePath = card.assetsBasePath,
                cardTextColor = card.textColor,
            )
            // Lock badge — round dark pill + padlock glyph, with the project's
            // gradient border ring (mask-composite) instead of a flat rgba edge.
            Box(
                modifier = Modifier
                    .mSize(40)
                    .mBorderRadius(100)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "50%")
                        property("left", "50%")
                        property("transform", "translate(-50%, -50%)")
                        property("background", "rgba(11, 22, 46, 0.8)")
                        property("backdrop-filter", "blur(4px)")
                        property("pointer-events", "none")
                    },
                contentAlignment = Alignment.Center
            ) {
                com.arena_web.ui.sections.profile.edit.GradientBorderRing(radiusPx = 100)
                SpanText("🔒", modifier = Modifier.mFontSize(18))
            }
        }
        // Figma 8383:187127 — price pill below the card.
        PricePill(price = card.price)
    }
}

@Composable
private fun PricePill(price: String) {
    val sportColors = LocalSportColors.current
    val formatted = if (price.startsWith("$")) price else "$ $price"
    // Per project convention (feedback_app_gradients_tokens): pill borders
    // use `AppGradients.ButtonBorderGradientPrimary` painted via the
    // mask-composite trick — never hardcoded rgba(255,255,255,X). Fill
    // sits on padding-box, gradient sits on border-box.
    Box(
        modifier = Modifier
            .mBorderRadius(22)
            .styleModifier {
                property("padding", "${m(4)} ${m(12)}")
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "linear-gradient(rgba(11, 22, 46, 0.8), rgba(11, 22, 46, 0.8)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            formatted,
            modifier = Modifier
                .mFontSize(16)
                .fontWeight(AppTypography.SemiBold)
                .mLineHeight(18)
                .color(sportColors.primary)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun HubSegmentedControl(
    active: HubTab,
    onSelect: (HubTab) -> Unit,
) {
    SegmentedControlShell {
        SegmentedIcon(AppIcons.Newspaper, active = active == HubTab.Content) { onSelect(HubTab.Content) }
        SegmentedIcon(AppIcons.Statistics, active = active == HubTab.Stats) { onSelect(HubTab.Stats) }
        SegmentedIcon(AppIcons.Cards, active = active == HubTab.Cards) { onSelect(HubTab.Cards) }
    }
}

@Composable
private fun StoriesRow(
    stories: List<UserStoryDto>,
    onStoryClick: (Int) -> Unit = {},
    onAddStoryClick: () -> Unit = {},
    canAddStory: Boolean = true,
) {
    // Figma 8376:97355 — Add button (42) + 4 stories (54 each) with a
    // tight 15-px gap between every item, anchored to the row start.
    // Earlier versions used `justify-between` which on real (wider) phones
    // pushed the stories all the way to the right edge with a huge empty
    // gap after the + button.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .mPadding(0, 8)
            .mGap(15),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Add story button — Figma 8376:97357: 42-px circle with a small
        // (~14 px) thin + glyph centered inside. Border keeps the project
        // gradient via mask-composite (see app-gradients-tokens rule). Hidden
        // when visiting another user's profile (read-only).
        if (canAddStory) {
            Box(
                modifier = Modifier
                    .mSize(42)
                    .mBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { onAddStoryClick() }
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Add, modifier = Modifier.mSize(14).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
            }
        }
        // Render only actual stories (own profile shows up to 4). No empty
        // placeholder slots — when stories is empty, only the + button remains.
        stories.take(4).forEachIndexed { idx, story ->
            StoryThumb(imageUrl = story.imageUrl, onClick = { onStoryClick(idx) })
        }
    }
}

@Composable
private fun StoryThumb(imageUrl: String?, onClick: () -> Unit = {}) {
    // Figma 8376:97360 — plain 54-px circular thumbnail, no ring/border.
    Box(
        modifier = Modifier
            .mSize(54)
            .mBorderRadius(100)
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                if (imageUrl != null) {
                    property("background-image", "url('$imageUrl')")
                    property("background-size", "cover")
                    property("background-position", "center")
                } else {
                    property("background-color", "WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40)")
                }
            }
    )
}

@Composable
private fun ViewModeButton(
    icon: String,
    active: Boolean,
    iconSize: Number = 18,
    onClick: () -> Unit = {},
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(
                if (active) Modifier.styleModifier {
                    property("background-color", sportColors.primary.toString())
                } else Modifier.styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
            ),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            src = icon,
            modifier = Modifier.mSize(iconSize).styleModifier {
                property(
                    "filter",
                    if (active) sportColors.iconFilterOnPrimary
                    else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V
                )
            }
        )
    }
}

@Composable
private fun AddPostButton(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    // Figma 8376:97376 — same elongated-pill silhouette as desktop AddPostPill
    // (PlayerHubSection.kt:1432): a 36-px circle on the left, a 6×13 Spacer
    // filling the seam, and a rounded-22 pill holding the label. Overlapping
    // negative margins (-4) absorb the rounded corners so the three shapes
    // read as one continuous pill at any scale.
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .mSize(36)
                .mBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center
        ) {
            // Figma 8376:97379 — Add icon container 24, Vector inset 18.75% →
            // visible 15. ic_add.svg viewBox 15×15 is tight, so we render at
            // the visible-glyph size.
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.mSize(15).styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
        // Spacer that fills the visual seam between the circle and the pill —
        // mirrors Figma 8376:97382 (6×13).
        Box(
            modifier = Modifier
                .mWidth(6)
                .mHeight(13)
                .backgroundColor(sportColors.primary)
                .styleModifier { property("margin-left", m(-4)) }
                .zIndex(0)
        )
        Box(
            modifier = Modifier
                .mHeight(36)
                .mBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .mPadding(10, 24)
                .styleModifier { property("margin-left", m(-4)) },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileContentMobileAddPost,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

// =============================================================================
// Posts — Grid view (3 columns of post-first-image tiles, multi-photo icon
// overlay) and List view (vertical card list mirroring desktop PostsList).
// =============================================================================

@Composable
private fun PostsGrid(posts: List<UserPostDto>) {
    // Figma 9305:347932 — 3-column grid with 4-px gaps. Iterate posts (not
    // flat images) so each tile carries its parent post and we can overlay
    // the multi-photo glyph for posts that hold more than one image.
    Div(attrs = {
        style {
            width(100.percent)
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(3, 1fr)")
            property("gap", m(4))
        }
    }) {
        posts.filter { it.images.isNotEmpty() }.take(9).forEach { post ->
            val img = post.images.first()
            val hasMultipleImages = post.images.size > 1
            Div(attrs = {
                style {
                    property("position", "relative")
                    property("width", "100%")
                    property("aspect-ratio", "1 / 1")
                    property("border-radius", m(7.79))
                    property("overflow", "hidden")
                    property("background-image", "url('${img.imageUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("cursor", "pointer")
                }
            }) {
                // Figma 9300:142883 — multi-photo glyph at the top-right.
                // The Figma icon-container is 24, but the Vector inside it has
                // 6.25 % inset (visible 21) AND our `ic_post_multi.svg` has
                // its drawing tucked into a 21×21 area of a 24×24 viewBox
                // (bottom-right anchored, ~3 px gap at top-left). Compound
                // effect: at mSize(24) the visible copy-stack would render at
                // ~21 px, too dominant relative to the tile. We render the
                // ICON itself at the size Figma intended for the glyph
                // (≈14 px), which on our padded SVG resolves to mSize(16).
                if (hasMultipleImages) {
                    Box(
                        modifier = Modifier.styleModifier {
                            property("position", "absolute")
                            property("top", m(6))
                            property("right", m(6))
                            property("width", m(16))
                            property("height", m(16))
                            property("pointer-events", "none")
                            property("background-image", "url('${AppIcons.PostMulti}')")
                            property("background-size", "contain")
                            property("background-repeat", "no-repeat")
                            property("background-position", "center")
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun PostsList(
    posts: List<UserPostDto>,
    userName: String,
    userAvatarUrl: String?,
    onDeleteClick: ((UserPostDto) -> Unit)? = null,
) {
    // Wrap in a Div that aliases `--scale` to `--mobile-scale` so any nested
    // `s()` helpers (e.g. inside PostImageCollage) resolve at mobile scale
    // instead of being clamped to 0.4.
    Div(attrs = {
        style {
            property("width", "100%")
            property("--scale", "var(--mobile-scale)")
        }
    }) {
        Column(modifier = Modifier.fillMaxWidth().mGap(28)) {
            posts.filter { it.images.isNotEmpty() || it.content.isNotBlank() }
                .forEach { post ->
                    PostListCard(
                        post = post,
                        userName = userName,
                        userAvatarUrl = userAvatarUrl,
                        onDelete = onDeleteClick?.let { { it(post) } },
                    )
                }
        }
    }
}

@Composable
private fun PostListCard(
    post: UserPostDto,
    userName: String,
    userAvatarUrl: String?,
    onDelete: (() -> Unit)? = null,
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().mGap(12)) {
        // Header: avatar + name/date stack
        Row(
            modifier = Modifier.fillMaxWidth().mGap(10),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .mSize(40)
                    .mBorderRadius(100)
                    .backgroundColor(AppColors.Dark.SurfaceVariant)
                    .overflow(Overflow.Hidden)
                    .styleModifier {
                        if (!userAvatarUrl.isNullOrBlank()) {
                            property("background-image", "url('$userAvatarUrl')")
                            property("background-size", "cover")
                            property("background-position", "center")
                        }
                    }
            )
            Column(modifier = Modifier.flexGrow(1).mGap(2)) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.mGap(6)
                ) {
                    SpanText(
                        userName.ifBlank { t.profileContentMobileUserFallback },
                        modifier = Modifier
                            .mFontSize(14)
                            .fontWeight(AppTypography.Medium)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                    // 3-px bullet
                    Box(
                        modifier = Modifier
                            .mSize(3)
                            .mBorderRadius(100)
                            .backgroundColor(AppColors.Text.Muted)
                    )
                    SpanText(
                        com.arena_web.ui.sections.profile.formatPostDate(post.createdAt),
                        modifier = Modifier
                            .mFontSize(13)
                            .fontWeight(AppTypography.Regular)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
                if (!post.city.isNullOrBlank()) {
                    SpanText(
                        post.city!!,
                        modifier = Modifier
                            .mFontSize(12)
                            .fontWeight(AppTypography.Light)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            }
            if (onDelete != null) {
                com.arena_web.ui.components.widgets.KebabDeleteMenu(onDelete = onDelete)
            }
        }
        // Body text
        if (post.content.isNotBlank()) {
            SpanText(
                post.content,
                modifier = Modifier
                    .fillMaxWidth()
                    .mFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .mLineHeight(20)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        // Images — reuses the desktop collage, scoped to mobile-scale by the
        // parent `--scale` override.
        if (post.images.isNotEmpty()) {
            com.arena_web.ui.components.feed.PostImageCollage(
                post.images.map { it.imageUrl },
                post.layout,
            )
        }
        // Like + comment summary
        Row(
            modifier = Modifier.mGap(18),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.mGap(6),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(src = AppIcons.Heart, modifier = Modifier.mSize(18).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
                SpanText(
                    "${post.likesCount}",
                    modifier = Modifier
                        .mFontSize(14)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
            Row(
                modifier = Modifier.mGap(6),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(src = AppIcons.Chat, modifier = Modifier.mSize(16).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
                SpanText(
                    "${post.commentsCount}",
                    modifier = Modifier
                        .mFontSize(14)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

// =============================================================================
// Shared primitives
// =============================================================================

@Composable
private fun ContainerCard(
    paddingTop: Number = 12,
    paddingBottom: Number = 14,
    paddingHorizontal: Number = 12,
    gap: Number = 22,
    blur: Boolean = false,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .mBorderRadius(22)
            .mPaddingLeft(paddingHorizontal).mPaddingRight(paddingHorizontal)
            .mPaddingTop(paddingTop).mPaddingBottom(paddingBottom)
            .mGap(gap)
            .styleModifier {
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property(
                    "background",
                    "linear-gradient(-20deg, rgba(52, 119, 250, 0.018) 27%, rgba(2, 4, 11, 0.3) 140%)"
                )
                if (blur) {
                    property("backdrop-filter", "blur(11.165px)")
                    property("-webkit-backdrop-filter", "blur(11.165px)")
                }
            }
    ) {
        content()
    }
}

@Composable
private fun SectionHeader(text: String, modifier: Modifier = Modifier) {
    SpanText(
        text,
        modifier = modifier
            .mFontSize(20)
            .fontWeight(AppTypography.Medium)
            .color(com.arena_web.ui.theme.ThemeVars.Text2)
            .styleModifier { property("line-height", m(26)) }
    )
}

@Composable
private fun SegmentedControlShell(content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .mHeight(44)
            .mBorderRadius(34)
            .mPaddingLeft(4).mPaddingRight(4).mPaddingTop(2).mPaddingBottom(2)
            .mGap(4)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center,
    ) {
        content()
    }
}

@Composable
private fun SegmentedIcon(
    icon: String,
    active: Boolean,
    onClick: () -> Unit,
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(
                if (active) Modifier.styleModifier {
                    property("background-color", sportColors.primary.toString())
                } else Modifier
            ),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            src = icon,
            modifier = Modifier.mSize(16).styleModifier {
                property(
                    "filter",
                    if (active) sportColors.iconFilterOnPrimary
                    else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V
                )
            }
        )
    }
}

private fun formatShortDate(iso: String): String = try {
    val d = kotlin.js.Date(iso)
    val day = d.getDate()
    val month = arrayOf(
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    )[d.getMonth()]
    "$day $month"
} catch (_: Throwable) {
    ""
}









































